/**
 * 静态阶段的 mock 数据。数仓 Holo BI 表就绪前，页面全部数据从这里来。
 *
 * 口径与量级对齐产品门户 product.prod.tita.ltd/bag-stay-monitor 的线上观感：
 * 五个环节的包裹量在万级、包牌数十级、超 24h 的流向数十个、最久停留数百小时。
 * 所有随机数走固定种子，刷新页面不会跳数，方便和产品逐格对照。
 *
 * todo hetianyang BI 表就绪后整个 mock/ 目录删除，useBagStayMonitorData 换成 GraphQL query。
 */
import type {
    BagRecord,
    BagStayMonitorData,
    DetailQuery,
    DetailStage,
    OpsAcctRow,
    OpsHourRow,
    OpsKind,
    ParcelDetail,
    ParcelRow,
    SorterDevice,
    SorterHourRow
} from '../types';
import { OPS_KINDS, OTHER_CUSTOMER, UNKNOWN_CUSTOMER } from '../constants';
import { filterParcels } from '../lib/detailFilter';
import { hubHourKey } from '../lib/format';
import { flowKey } from '../lib/flow';
import type { BatchStub } from '../lib/tree';
import type { BatchStatus } from '../constants';

/** 固定种子的伪随机数，保证每次渲染数字一致 */
function mulberry32(seed: number): () => number {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function hashStr(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return h;
}

export const MOCK_HUBS = ['LAX', 'EWR'];

/** todo hetianyang hub 时区应由后端随 hub 配置下发，这里先用静态表 */
export const HUB_ZONE: Record<string, string> = {
    LAX: 'America/Los_Angeles',
    EWR: 'America/New_York'
};

export const hubZone = (hub: string): string => HUB_ZONE[hub] ?? 'America/Los_Angeles';

const CUSTOMERS = ['TEMU', 'SHEIN', 'TikTok Shop', 'Anker', 'Newegg', OTHER_CUSTOMER, UNKNOWN_CUSTOMER];

/** 客户在包裹量里的占比，和 buildParcelRows 保持一致 */
const CUSTOMER_WEIGHT = [0.42, 0.24, 0.1, 0.1, 0.1, 0.06, 0.03];

/** 干线目的地：别的 HUB */
const TRUNK_DESTS: Record<string, string[]> = {
    LAX: ['ORD', 'CVG', 'HOU', 'DFW', 'ATL', 'MIA', 'EWR', 'PHX', 'SEA', 'DEN', 'BOS', 'IAH'],
    EWR: ['ORD', 'CVG', 'ATL', 'MIA', 'LAX', 'BOS', 'DFW', 'PHX']
};

/** 支线仓及其线路首字母 */
const BRANCH_WHS: Record<string, { wh: string; letters: string[] }[]> = {
    LAX: [
        { wh: 'COI', letters: ['A', 'B', 'C', 'D'] },
        { wh: 'LADC', letters: ['H', 'J', 'K'] },
        { wh: 'SAN', letters: ['A', 'B'] },
        { wh: 'ONT', letters: ['A', 'B', 'C'] },
        { wh: 'SNA', letters: ['A', 'B'] },
        { wh: 'RIV', letters: ['A', 'C'] },
        { wh: 'BUR', letters: ['A', 'B'] },
        { wh: 'LGB', letters: ['A'] },
        { wh: 'PMD', letters: ['A', 'B'] },
        { wh: 'OXR', letters: ['A'] }
    ],
    EWR: [
        { wh: 'MHT', letters: ['A', 'B'] },
        { wh: 'PHL', letters: ['A', 'B', 'C'] },
        { wh: 'BWI', letters: ['A'] },
        { wh: 'ALB', letters: ['A', 'B'] },
        { wh: 'PIT', letters: ['A'] }
    ]
};

interface FlowSpec {
    /** 原始三段码 */
    code: string;
    branch: boolean;
}

/** 列出一个仓的全部叶子流向 */
function flowsOf(hub: string): FlowSpec[] {
    const pre = hub === 'LAX' ? 'LA' : hub;
    const out: FlowSpec[] = [];
    for (const d of TRUNK_DESTS[hub] ?? []) out.push({ code: `${d}-${hub}-B002`, branch: false });
    for (const { wh, letters } of BRANCH_WHS[hub] ?? []) {
        for (const l of letters) out.push({ code: `${pre}-${wh}-${l}03`, branch: true });
        // 只有二段、没有三段字母的那一档
        out.push({ code: `${pre}-${wh}`, branch: true });
    }
    return out;
}

/** 每个环节的包裹量基数与停留小时上限，用来让各环节的量级和线上接近 */
const STAGE_PROFILE: Record<number, { base: number; spread: number; maxDwellH: number }> = {
    0: { base: 420, spread: 900, maxDwellH: 711 },
    1: { base: 340, spread: 700, maxDwellH: 704 },
    2: { base: 2100, spread: 4200, maxDwellH: 267 },
    3: { base: 430, spread: 880, maxDwellH: 362 },
    4: { base: 460, spread: 940, maxDwellH: 1.6 }
};

const H = 3_600_000;

function buildParcelRows(hub: string, now: number): ParcelRow[] {
    const rows: ParcelRow[] = [];
    const flows = flowsOf(hub);
    flows.forEach((f, fi) => {
        const k = flowKey(hub, f.code);
        const rnd = mulberry32(hashStr(`${hub}|${f.code}`));
        // 前 30 个流向里挑一部分做成「超 24h」的，和线上「30 个流向」的观感对齐
        const isHot = fi < 30;
        for (const stageStr of Object.keys(STAGE_PROFILE)) {
            const stage = Number(stageStr) as DetailStage;
            const prof = STAGE_PROFILE[stage];
            // 该流向在该环节的最长停留
            const dwellH = isHot ? prof.maxDwellH * (0.25 + rnd() * 0.75) : rnd() * 12;
            const oldestMs = now - dwellH * H;
            CUSTOMERS.forEach((cust, ci) => {
                const weight = CUSTOMER_WEIGHT[ci];
                const parcels = Math.round((prof.base + rnd() * prof.spread) * weight);
                if (parcels <= 0) return;
                const over24 = dwellH > 24 ? Math.round(parcels * (0.3 + rnd() * 0.5)) : 0;
                rows.push({
                    hub,
                    stage,
                    flow: f.code,
                    g1: k.group,
                    g2: k.child,
                    branch: !k.trunk,
                    customer: cust,
                    parcels,
                    over24,
                    bags: stage === 0 ? Math.round(parcels * 0.8) : undefined,
                    oldest: new Date(oldestMs + ci * 7 * 60_000).toISOString()
                });
            });
        }
        // 错分未重装（stage 5，计在 ① 里）：只有一部分流向有
        if (rnd() < 0.35) {
            rows.push({
                hub,
                stage: 5,
                flow: f.code,
                g1: k.group,
                g2: k.child,
                branch: !k.trunk,
                customer: CUSTOMERS[ci6(rnd)],
                parcels: 3 + Math.round(rnd() * 28),
                over24: 0,
                oldest: new Date(now - (6 + rnd() * 60) * H).toISOString()
            });
        }
        // 异常包裹（stage 6，从各环节剔除后单列）
        if (rnd() < 0.45) {
            rows.push({
                hub,
                stage: 6,
                flow: f.code,
                g1: k.group,
                g2: k.child,
                branch: !k.trunk,
                customer: CUSTOMERS[ci6(rnd)],
                parcels: 1 + Math.round(rnd() * 14),
                over24: 0,
                oldest: new Date(now - (12 + rnd() * 400) * H).toISOString()
            });
        }
    });
    return rows;
}

function ci6(rnd: () => number): number {
    return Math.min(CUSTOMERS.length - 1, Math.floor(rnd() * CUSTOMERS.length));
}

const BAG_RESULTS: ('已装包未发件' | '已发件未装车' | '已装车未发车')[] = [
    '已装包未发件',
    '已发件未装车',
    '已装车未发车'
];

/** 各环节的在途包牌数，和线上 64 / 12 / 3 的量级对齐 */
const BAG_COUNT: Record<string, number> = {
    已装包未发件: 64,
    已发件未装车: 12,
    已装车未发车: 3
};

function buildBags(hub: string, now: number): BagRecord[] {
    const flows = flowsOf(hub);
    const out: BagRecord[] = [];
    for (const result of BAG_RESULTS) {
        const n = BAG_COUNT[result];
        const rnd = mulberry32(hashStr(`${hub}|bag|${result}`));
        const maxDwellH = result === '已装包未发件' ? 267 : result === '已发件未装车' ? 362 : 1.6;
        for (let i = 0; i < n; i++) {
            const f = flows[Math.floor(rnd() * flows.length)];
            const parcelCount = 40 + Math.round(rnd() * 900);
            const customers: Record<string, number> = {};
            let left = parcelCount;
            for (let c = 0; c < 3 && left > 0; c++) {
                const cust = CUSTOMERS[Math.floor(rnd() * CUSTOMERS.length)];
                const take = c === 2 ? left : Math.max(1, Math.round(left * (0.3 + rnd() * 0.5)));
                customers[cust] = (customers[cust] ?? 0) + Math.min(take, left);
                left -= take;
            }
            out.push({
                // 跨环节连续编号：行末「明细」会把 ②~④ 的包牌合在一起，编号重复会撞 rowKey
                bagNumber: `${hub}${String(20261016).slice(2)}${String(1000 + out.length)}`,
                siteName: `${hub} HUB`,
                siteBusinessType: '分拣仓',
                bagBusinessType: f.branch ? '尾程业务' : '分拣业务',
                sortingRule: f.code,
                carrierName: f.branch ? 'SwiftX LM' : 'SwiftX LH',
                parcelCount,
                bagResult: result,
                customers,
                stageEnteredAt: new Date(now - maxDwellH * (0.1 + rnd() * 0.9) * H).toISOString()
            });
        }
    }
    return out;
}

/** 包牌已发、内部还有包裹没发走的包牌 */
function buildStragglers(hub: string, now: number): BagRecord[] {
    const flows = flowsOf(hub);
    const rnd = mulberry32(hashStr(`${hub}|strag`));
    const out: BagRecord[] = [];
    for (let i = 0; i < 30; i++) {
        const f = flows[Math.floor(rnd() * flows.length)];
        const parcelCount = 2 + Math.round(rnd() * 40);
        const main = CUSTOMERS[ci6(rnd)];
        const customers: Record<string, number> = { [main]: parcelCount };
        if (parcelCount > 10) {
            const other = CUSTOMERS[ci6(rnd)];
            const n = Math.round(parcelCount * 0.3);
            customers[main] -= n;
            customers[other] = (customers[other] ?? 0) + n;
        }
        out.push({
            bagNumber: `${hub}STR${String(2000 + i)}`,
            siteName: `${hub} HUB`,
            siteBusinessType: '分拣仓',
            bagBusinessType: f.branch ? '尾程业务' : '分拣业务',
            sortingRule: f.code,
            carrierName: 'SwiftX LM',
            parcelCount,
            bagResult: '已装包未发件',
            customers,
            stageEnteredAt: new Date(now - (2 + rnd() * 120) * H).toISOString()
        });
    }
    return out;
}

/** 作业账号。role / name 是后端原样返回的数据值，前端不翻译；role 为 null 表示系统 / 未知 */
const ACCOUNTS: { name: string | null; role: string | null }[] = [
    { name: 'li.wei', role: 'HUB_OPERATOR' },
    { name: 'zhang.hao', role: 'HUB_OPERATOR' },
    { name: 'wang.fang', role: 'HUB_OPERATOR' },
    { name: 'chen.jie', role: 'HUB_LEADER' },
    { name: 'liu.yang', role: 'HUB_OPERATOR' },
    { name: 'CB-01', role: 'CROSS_BELT' },
    { name: 'CB-02', role: 'CROSS_BELT' },
    { name: null, role: null }
];

/** 分拣机管理表里的设备：LAX 两台交叉带 + 两台 DWS；EWR 不配置，用来展示「该仓暂未配置」 */
const SORTER_DEVICES: SorterDevice[] = [
    { hub: 'LAX', sorterCode: 'CB-01', sorterName: 'LAX Cross-belt 1', sorterType: 2 },
    { hub: 'LAX', sorterCode: 'CB-02', sorterName: 'LAX Cross-belt 2', sorterType: 5 },
    { hub: 'LAX', sorterCode: 'DWS-01', sorterName: 'LAX DWS 1', sorterType: 1 },
    { hub: 'LAX', sorterCode: 'DWS-02', sorterName: null, sorterType: 1 }
];

/** 交叉带设计产能，和 SorterPanel 的标红线一致，让 mock 里一部分小时低于产能 */
const CROSS_BELT_CAPACITY = 25000;

interface OpsMock {
    hours: OpsHourRow[];
    accts: OpsAcctRow[];
    sorter: SorterHourRow[];
    devices: SorterDevice[];
}

function buildOps(hub: string, now: number): OpsMock {
    const zone = hubZone(hub);
    const hours: OpsHourRow[] = [];
    const accts: OpsAcctRow[] = [];
    const sorter: SorterHourRow[] = [];
    const devices = SORTER_DEVICES.filter((d) => d.hub === hub);
    for (let i = 47; i >= 0; i--) {
        const ms = now - i * H;
        const hourKey = hubHourKey(ms, zone);
        const localHour = Number(hourKey.slice(11));
        // 夜班（18:00-06:00）是作业高峰
        const shiftFactor = localHour >= 18 || localHour < 6 ? 1 : 0.35;
        for (const kd of OPS_KINDS) {
            const rnd = mulberry32(hashStr(`${hub}|${hourKey}|${kd.k}`));
            const base = kd.k === 'bigbag' ? 900 : kd.k === 'bag' ? 9000 : 6000;
            const count = Math.round(base * shiftFactor * (0.55 + rnd() * 0.9));
            // 装包分设备 / 人工两行；大包到仓是设备扫描；其余按人工扫描造
            const manualSplit: [0 | 1, number][] =
                kd.k === 'bag' ? [[0, 0.62], [1, 0.38]] : kd.k === 'bigbag' ? [[0, 1]] : [[1, 1]];
            CUSTOMERS.forEach((customer, ci) => {
                for (const [manual, share] of manualSplit) {
                    const n = Math.round(count * CUSTOMER_WEIGHT[ci] * share);
                    if (n <= 0) continue;
                    // 入库 / 发车事件没有包牌号；大包到仓的袋数是客户大包数
                    const bags =
                        kd.k === 'in' || kd.k === 'out' ? 0 : kd.k === 'bigbag' ? Math.max(1, Math.round(n / 25)) : Math.round(n / 420);
                    hours.push({ hub, hourKey, kind: kd.k, manual, customer, count: n, bags });
                }
            });
            if (kd.k === 'bigbag') continue;
            // 按账号拆分：每个账号的量再拆给 2 个客户，最后一档合成「其他账号」
            let left = count;
            ACCOUNTS.forEach((a, ai) => {
                if (left <= 0) return;
                const take = Math.min(left, Math.round(count * (0.06 + rnd() * 0.16)));
                if (take <= 0) return;
                left -= take;
                const c1 = CUSTOMERS[ai % CUSTOMERS.length];
                const c2 = CUSTOMERS[(ai + 1) % CUSTOMERS.length];
                const n1 = Math.round(take * 0.7);
                const base = { hub, hourKey, kind: kd.k as Exclude<OpsKind, 'bigbag'>, role: a.role, name: a.name, isOther: false };
                accts.push({ ...base, customer: c1, count: n1 });
                if (take - n1 > 0) accts.push({ ...base, customer: c2, count: take - n1 });
            });
            if (left > 0) {
                accts.push({
                    hub,
                    hourKey,
                    kind: kd.k as Exclude<OpsKind, 'bigbag'>,
                    role: null,
                    name: null,
                    isOther: true,
                    customer: CUSTOMERS[0],
                    count: left
                });
            }
        }
        for (const d of devices) {
            const rnd = mulberry32(hashStr(`${hub}|${hourKey}|${d.sorterCode}`));
            const dur = shiftFactor === 1 ? 0.85 + rnd() * 0.15 : 0.3 + rnd() * 0.4;
            if (d.sorterType === 1) {
                sorter.push({
                    ...emptySorterRow(hub, d, hourKey),
                    dwsScanCount: Math.round(dur * 3000 * (0.7 + rnd() * 0.5)),
                    dwsDurationHours: Number(dur.toFixed(2))
                });
                continue;
            }
            const scan = Math.round(dur * CROSS_BELT_CAPACITY * (0.72 + rnd() * 0.45));
            const recognized = Math.round(scan * (0.93 + rnd() * 0.05));
            const distinct = Math.round(recognized * (0.9 + rnd() * 0.06));
            const dropDistinct = Math.round(distinct * (0.92 + rnd() * 0.06));
            sorter.push({
                ...emptySorterRow(hub, d, hourKey),
                crossBeltScanCount: scan,
                crossBeltRecognizedTrackingCount: recognized,
                crossBeltDistinctTrackingCount: distinct,
                crossBeltDurationHours: Number(dur.toFixed(2)),
                dropCount: Math.round(dropDistinct * 1.02),
                dropDistinctTrackingCount: dropDistinct,
                dropInExpectChuteDistinctCount: Math.round(dropDistinct * (0.93 + rnd() * 0.06))
            });
        }
    }
    return { hours, accts, sorter, devices };
}

function emptySorterRow(hub: string, d: SorterDevice, hourKey: string): SorterHourRow {
    return {
        hub,
        sorterCode: d.sorterCode,
        sorterType: d.sorterType,
        hourKey,
        dwsScanCount: null,
        dwsDurationHours: null,
        crossBeltScanCount: null,
        crossBeltRecognizedTrackingCount: null,
        crossBeltDistinctTrackingCount: null,
        crossBeltDurationHours: null,
        dropCount: null,
        dropDistinctTrackingCount: null,
        dropInExpectChuteDistinctCount: null
    };
}

/** 示例批次。批次模块未上线，数字全是 0，只用来把四层结构搭出来。 */
const BATCH_STATUSES: BatchStatus[] = ['分拣中', '分拣结束', '已完成', '已取消'];

export function mockBatches(prefix: string, wh: string, now: number, timeZone = 'America/Los_Angeles'): BatchStub[] {
    const base = hashStr(wh);
    const slots: [number, string][] = [
        [0, '0930'],
        [0, '0610'],
        [1, '2240']
    ];
    const yyyymmdd = (ms: number): string =>
        new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        })
            .format(new Date(ms))
            .replace(/-/g, '');
    return slots.map(([daysAgo, hhmm], i) => ({
        no: `${prefix}_${yyyymmdd(now - daysAgo * 86_400_000)}_${hhmm}_${prefix}-${wh}`,
        status: BATCH_STATUSES[(base + i) % BATCH_STATUSES.length]
    }));
}

export function buildMockData(hub: string, now: number): BagStayMonitorData {
    const ops = buildOps(hub, now);
    return {
        // 线上是「10 分钟缓存 + 后台刷新」，这里把计算时间固定回退 4 分钟，让「最近更新时间」看起来合理
        computedAt: new Date(now - 4 * 60_000).toISOString(),
        refreshing: false,
        overdueThresholdHours: 24,
        detailPerCell: 100,
        hubs: MOCK_HUBS,
        customers: CUSTOMERS,
        bags: buildBags(hub, now),
        stragglerBags: buildStragglers(hub, now),
        parcels: buildParcelRows(hub, now),
        opsHours: ops.hours,
        opsAccounts: ops.accts,
        sorterHours: ops.sorter,
        sorterDevices: ops.devices
    };
}

const EXC_REASONS = ['仓库内疑似丢失', '长时间未找到(HUB)', '长时间未找到(仓库)', '丢件', '司机丢失', '仓内丢失', '损坏(HUB)'];
const STATUS_DESC = ['运输中', '已到达分拣中心', '分拣中', '已装包', '已发运', '待装车'];

/**
 * 明细下钻。真实实现是后端按 hub/stage/g1/g2/br/customer 查明细表，这里按同样的入参合成。
 * 条数与排序规则对齐线上：按停留从久到短，最多 limit 条（默认 300）。
 */
export function buildMockDetails(q: DetailQuery, now: number): ParcelDetail[] {
    const limit = Math.min(Math.max(q.limit ?? 300, 1), 5000);
    const flows = flowsOf(q.hub).filter((f) => {
        const k = flowKey(q.hub, f.code);
        if (q.branch !== undefined && q.branch === k.trunk) return false;
        if (q.g1 !== undefined && k.group !== q.g1) return false;
        if (q.g2 !== undefined && (k.child ?? '') !== q.g2) return false;
        return true;
    });
    if (flows.length === 0) return [];
    const rnd = mulberry32(hashStr(`${q.hub}|${q.stage}|${q.g1 ?? ''}|${q.g2 ?? ''}|${q.customer ?? ''}`));
    const prof = STAGE_PROFILE[q.stage === 5 || q.stage === 6 ? 1 : q.stage];
    const maxDwellH = q.stage === 6 ? 400 : (prof?.maxDwellH ?? 24);
    const n = Math.min(limit, q.stage === 5 ? 60 : q.stage === 6 ? 120 : 300);
    const out: ParcelDetail[] = [];
    for (let i = 0; i < n; i++) {
        const f = flows[Math.floor(rnd() * flows.length)];
        const k = flowKey(q.hub, f.code);
        const customer = q.customer ?? CUSTOMERS[Math.floor(rnd() * CUSTOMERS.length)];
        const enteredMs = now - maxDwellH * H * (1 - i / n) * (0.6 + rnd() * 0.4);
        const isExc = q.stage === 6;
        out.push({
            hub: q.hub,
            stage: q.stage,
            flow: f.code,
            g1: k.group,
            g2: k.child,
            branch: !k.trunk,
            customer,
            trackingNumber: `SX${String(10_000_000_000 + Math.floor(rnd() * 8_999_999_999))}`,
            bagNumber:
                q.stage === 0
                    ? `BB${String(80_000_000 + Math.floor(rnd() * 9_999_999))}`
                    : q.stage >= 2 && q.stage <= 4
                      ? `${q.hub}2610161${String(100 + Math.floor(rnd() * 800))}`
                      : null,
            status: STATUS_DESC[Math.floor(rnd() * STATUS_DESC.length)],
            reason: isExc ? EXC_REASONS[Math.floor(rnd() * EXC_REASONS.length)] : null,
            markedAt: isExc ? new Date(enteredMs).toISOString() : null,
            enteredAt: new Date(enteredMs).toISOString()
        });
    }
    // 抽屉里的筛选在生成之后再过滤，不进随机种子：筛选前看到的单号，筛选后还能搜到
    return filterParcels(out, q).sort((a, b) => a.enteredAt.localeCompare(b.enteredAt));
}
